import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { TwitchLogo, DiscordLogo, YoutubeLogo, TiktokLogo, InstagramLogo, XLogo, ArrowRight } from "@phosphor-icons/react";
import { Particles } from "./Particles";
import { LiveBadge } from "./LiveBadge";
import { StreamTimeBanner } from "./StreamTimeBanner";
import { SpinningLogo } from "./SpinningLogo";
import { FollowerCount } from "./FollowerCount";
import { SOCIALS } from "../config/socials";
import { photoById } from "../config/photos";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/2a8fecd8-a911-4a16-acd3-b19129c23ab8/images/d83142002e6b606d55136532e6ee4c1544d5fb03a2480e46b1de601ecd697db7.png";

const socials = [
    { label: "Twitch", icon: TwitchLogo, href: SOCIALS.twitch.url, testId: "social-twitch" },
    { label: "Discord", icon: DiscordLogo, href: SOCIALS.discord.url, testId: "social-discord" },
    { label: "YouTube", icon: YoutubeLogo, href: SOCIALS.youtube.url, testId: "social-youtube" },
    { label: "TikTok", icon: TiktokLogo, href: SOCIALS.tiktok.url, testId: "social-tiktok" },
    { label: "Instagram", icon: InstagramLogo, href: SOCIALS.instagram.url, testId: "social-instagram" },
    { label: "X", icon: XLogo, href: SOCIALS.x.url, testId: "social-x" },
];

export const Hero = () => {
    return (
        <section
            className="relative min-h-[92svh] pt-32 pb-20 overflow-hidden"
            data-testid="hero-section"
        >
            <div className="absolute inset-0 z-0">
                <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-25" />
                <div className="absolute inset-0 bg-gradient-to-b from-obsidian-950/60 via-obsidian-950/80 to-obsidian-950" />
                <div className="aurora" />
                <div className="absolute inset-0 grid-bg opacity-40" />
            </div>
            <Particles density={28} />

            <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 lg:grid lg:grid-cols-[1fr_auto] lg:gap-10 lg:items-center">
                <div>
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="flex items-center gap-3 mb-10 flex-wrap"
                    data-testid="hero-status-row"
                >
                    <LiveBadge />
                    <span className="text-white/30 text-sm">·</span>
                    <span className="eyebrow">Streams daily · 11 AM EST</span>
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.05 }}
                    className="font-display leading-[0.95] tracking-tighter"
                    data-testid="hero-title"
                >
                    <span className="block text-5xl sm:text-6xl lg:text-7xl text-white font-semibold">
                        Daily streams.
                    </span>
                    <span className="block text-5xl sm:text-6xl lg:text-7xl gradient-text font-semibold mt-2">
                        A real community.
                    </span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="max-w-2xl text-base lg:text-lg text-white/65 leading-relaxed mt-7"
                    data-testid="hero-subtitle"
                >
                    I'm <span className="text-white">itz_ool</span>, a variety streamer on Twitch building toward a top-50 spot. One stream at a time, with the chat right there with me. Pull up.
                </motion.p>

                {/* Localized stream-time banner */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="mt-8 max-w-3xl"
                >
                    <StreamTimeBanner />
                </motion.div>

                {/* Live follower count */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.35 }}
                    className="mt-4 max-w-3xl"
                >
                    <FollowerCount />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="flex flex-wrap items-center gap-3 mt-8"
                    data-testid="hero-ctas"
                >
                    <Link
                        to="/live"
                        className="btn-sweep inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-black text-sm font-semibold hover:bg-brand-cyan transition-colors"
                        data-testid="hero-watch-live-btn"
                    >
                        Watch Live <ArrowRight size={14} weight="bold" />
                    </Link>
                    <a
                        href={SOCIALS.discord.url}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-sweep inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/5 border border-white/10 text-white text-sm font-medium hover:border-white/30 transition"
                        data-testid="hero-discord-btn"
                    >
                        <DiscordLogo size={14} weight="fill" />
                        Join the Discord
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="flex items-center gap-2 mt-10 flex-wrap"
                    data-testid="hero-social-buttons"
                >
                    <span className="eyebrow mr-2">Follow</span>
                    {socials.map((s) => (
                        <a
                            key={s.label}
                            href={s.href}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={s.label}
                            className="h-9 w-9 rounded-full bg-white/5 border border-white/10 hover:border-white/30 flex items-center justify-center text-white/70 hover:text-white transition"
                            data-testid={s.testId}
                        >
                            <s.icon size={14} weight="fill" />
                        </a>
                    ))}
                </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    className="hidden lg:flex flex-col items-center justify-center gap-6 pl-6"
                    data-testid="hero-spinning-logo-wrap"
                >
                    <SpinningLogo size={300} />
                    {/* Small framed photo of itz_ool */}
                    <motion.figure
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.45 }}
                        whileHover={{ y: -3, rotate: -1 }}
                        className="relative overflow-hidden rounded-2xl border border-cyan-400/40 bg-obsidian-900 w-[200px]"
                        style={{ boxShadow: "0 0 32px rgba(34,211,238,0.28)" }}
                        data-testid="hero-photo"
                    >
                        <img
                            src={photoById("cockpit").src}
                            alt={photoById("cockpit").alt}
                            loading="lazy"
                            className="w-full h-[240px] object-cover"
                        />
                        <div
                            aria-hidden
                            className="absolute inset-0 pointer-events-none"
                            style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85), transparent 55%)" }}
                        />
                        <figcaption className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                            <span className="font-display text-white text-sm leading-tight">itz_ool</span>
                            <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-cyan-300">irl</span>
                        </figcaption>
                    </motion.figure>
                </motion.div>
            </div>
        </section>
    );
};
